import type { CertificateRequestIntentDto } from "@/services/certificateRequestIntentsService";
import { getCertificateRequestIntents } from "@/services/certificateRequestIntentsService";
import { apiClient } from "@/services/apiClient";

export type CertificatePricingCatalogItem = {
  targetType: string;
  displayName: string;
  involvesAccountant: boolean;
  involvesNotary: boolean;
  /** Effective allowed fulfillment modes for this product (backend resolves catalog defaults). */
  fulfillmentModesAllowed: string[];
  description: string | null;
  /** Flat part of base (centésimos), before margin. */
  fixedAmountMinor?: number;
  /** Itemized fixed costs with labels for breakdown display. */
  fixedCostItems?: Array<{ label: string; amountMinor: number; includesIva: boolean }> | null;
  /** UR count from product definition (e.g. 2). */
  urUnits?: number;
  /** Which UR index: 'dgi' or 'arancel'. */
  urType?: string;
  /** UR variable part of base (centésimos), before margin. */
  urVariableAmountMinor?: number;
  baseAmountMinor: number | null;
  finalAmountMinor: number | null;
  serviceFinalAmountMinor: number | null;
  perFileStorageAmountMinor: number;
  /** Physical delivery component (centésimos UYU); not charged when fulfillment is digital-only. */
  deliveryFeeMinor?: number;
  /** Professional stamp / timbre cost (centésimos UYU); no IVA. 0 = not applicable. */
  professionalStampMinor?: number;
  /** Catalog flags for how stored amounts relate to VAT (used for display and future breakdown logic). */
  fixedAmountIncludesIva?: boolean;
  urVariableIncludesIva?: boolean;
  perFileStorageIncludesIva?: boolean;
  deliveryFeeIncludesIva?: boolean;
  pricingModel: "flat" | "per_file";
  currency: string;
  sortOrder: number;
  defaultBusinessDays: number;
};

export function catalogCustomerAmountMinor(item: CertificatePricingCatalogItem): number | null {
  return item.finalAmountMinor ?? item.baseAmountMinor;
}

export function getCertificatePricingCatalog(): Promise<CertificatePricingCatalogItem[]> {
  return apiClient
    .get<CertificatePricingCatalogItem[]>("/public/certificate-pricing/catalog")
    .then((res) => res.data);
}

export function minResolvedFinalPriceForCategory(
  catalog: CertificatePricingCatalogItem[],
  category: "accountant" | "notary"
): { amountMinor: number; currency: string } | null {
  const inCategory = catalog.filter((c) =>
    category === "accountant" ? c.involvesAccountant : c.involvesNotary
  );
  const withAmount = inCategory.filter((c) => catalogCustomerAmountMinor(c) != null);
  if (withAmount.length === 0) return null;
  let best = withAmount[0]!;
  for (const item of withAmount) {
    const a = catalogCustomerAmountMinor(item)!;
    const b = catalogCustomerAmountMinor(best)!;
    if (a < b) best = item;
  }
  return { amountMinor: catalogCustomerAmountMinor(best)!, currency: best.currency };
}

export function catalogItemForType(
  catalog: CertificatePricingCatalogItem[],
  targetType: string
): CertificatePricingCatalogItem | undefined {
  return catalog.find((c) => c.targetType === targetType);
}

const UYU_MINOR_PER_PESO = 100;

function roundMinorUpToWholePeso(minor: number): number {
  return Math.ceil(minor / UYU_MINOR_PER_PESO) * UYU_MINOR_PER_PESO;
}

/**
 * Mirrors backend: margin on fixed+UR only, then N × per-file storage (no margin), then round total up to whole peso.
 */
/** Must match backend `CreateCertificateCheckoutDto` max. */
export const MAX_INCOME_CERTIFICATE_BILLABLE_FILES = 100;

export function countIncomeCertificateBillableFiles(
  uploads: Array<{ type: string }>
): number {
  return uploads.filter(
    (u) => u.type === "income_invoice" || u.type === "income_salary_receipt"
  ).length;
}

/**
 * Matches backend checkout: `digital_only` does not pay the physical delivery component (subtracted);
 * `physical_only` and `digital_and_physical` pay the full computed total.
 */
export function applyCatalogDigitalDeliveryDeduction(
  totalMinor: number,
  item: CertificatePricingCatalogItem,
  fulfillmentMode?: string,
): number {
  const fee = item.deliveryFeeMinor ?? 0;
  if (fulfillmentMode === "digital_only" && fee > 0) {
    return Math.max(0, totalMinor - fee);
  }
  return totalMinor;
}

export function estimatePerFileCertificateTotalMinor(
  item: CertificatePricingCatalogItem | undefined,
  billableFileCount: number,
  fulfillmentMode?: string,
): number | null {
  if (!item) return null;
  const service =
    item.serviceFinalAmountMinor ?? item.finalAmountMinor ?? item.baseAmountMinor;
  if (service == null || !Number.isFinite(billableFileCount) || billableFileCount < 1) {
    return null;
  }
  if (item.pricingModel !== "per_file" || item.perFileStorageAmountMinor <= 0) {
    return applyCatalogDigitalDeliveryDeduction(service, item, fulfillmentMode);
  }
  const storageMinor = billableFileCount * item.perFileStorageAmountMinor;
  const totalMinor = roundMinorUpToWholePeso(service + storageMinor);
  return applyCatalogDigitalDeliveryDeduction(totalMinor, item, fulfillmentMode);
}

export function formatCatalogAmountMinor(
  amountMinor: number,
  currency: string,
  locale: string
): string {
  const code = currency.length === 3 ? currency : "UYU";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amountMinor / 100);
  } catch {
    const n = (amountMinor / 100).toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return `${code} ${n}`;
  }
}

export type CertificateWizardPricingDetailLine =
  | { type: "regulated_base"; amountMinor: number }
  | { type: "fixed_component"; amountMinor: number; label?: string }
  | { type: "ur_component"; amountMinor: number; urUnits: number; urType?: string }
  | { type: "service_fee"; amountMinor: number }
  | { type: "certificate_service"; amountMinor: number }
  /** @deprecated Prefer `income_margin_and_storage` for income per-file UI; kept for older breakdowns. */
  | { type: "document_storage"; amountMinor: number; documentCount: number }
  /** Income certificate: catalog base before margin (same idea as regulated_base, income copy). */
  | { type: "income_base"; amountMinor: number }
  /** Income certificate: margin on base (catalog %), without per-file storage. */
  | { type: "income_service_margin"; amountMinor: number }
  /** Income certificate: margin on base plus per-file storage in one line (no separate storage row). */
  | { type: "income_margin_and_storage"; amountMinor: number; documentCount: number }
  /** Professional stamp / timbre (no IVA). */
  | { type: "professional_stamp"; amountMinor: number };

export type CertificateWizardPricingBreakdownData = {
  currency: string;
  detailLines: CertificateWizardPricingDetailLine[];
  totalMinor: number;
  /**
   * Physical delivery / coordination fee (additive row) when fulfillment is not digital-only.
   * Detail lines use service minus this fee so base + this row matches the charged total.
   */
  deliveryIncludedMinor: number | null;
};

function deliveryDisplayFlags(
  _deliveryFeeMinor: number,
  _fulfillmentMode: string | undefined
): {
  deliveryIncludedMinor: number | null;
} {
  // Delivery is baked into `baseAmountMinor` / `fixedAmountMinor` for physical
  // checkouts by operator convention. Showing it as a separate additive row
  // double-counted it visually (base appeared stripped of delivery and a
  // delivery row was added on top). The wizard now displays base/fixed AS-IS
  // and digital-only mode subtracts the delivery slice from those rows
  // directly, so no separate row is needed.
  return { deliveryIncludedMinor: null };
}

/**
 * Backend convention: `serviceFinalAmountMinor` and the source fields
 * (`baseAmountMinor`, `fixedAmountMinor`, fixed-item amounts) already include the
 * physical-delivery slice for physical checkouts — there is no separate delivery
 * row to add on top. For digital-only mode we subtract the delivery fee from the
 * displayed base/fixed component and from the total so the visible row sum
 * still equals the charged amount.
 *
 * Row set:
 *   [base or fixed/UR components, as charged] + [margin] + [stamp]
 *   = charged total for the selected fulfillment mode
 */
export function buildFlatWizardPricingBreakdown(
  item: CertificatePricingCatalogItem | undefined,
  fulfillmentMode?: string
): CertificateWizardPricingBreakdownData | null {
  if (!item) return null;
  const service =
    item.serviceFinalAmountMinor ?? item.finalAmountMinor ?? item.baseAmountMinor;
  if (service == null) return null;
  const currency = item.currency;
  const fee = item.deliveryFeeMinor ?? 0;
  const stampMinor = item.professionalStampMinor ?? 0;
  const isDigitalOnly = fulfillmentMode === "digital_only";
  const digitalAdjustment = isDigitalOnly ? fee : 0;
  const totalMinor = Math.max(0, service - digitalAdjustment);

  const base = item.baseAmountMinor;
  const fixed = item.fixedAmountMinor ?? 0;
  const urVar = item.urVariableAmountMinor ?? 0;
  const urCount = item.urUnits ?? 0;

  const detailLines: CertificateWizardPricingDetailLine[] = [];

  const canSplitBase =
    base != null && (fixed > 0 || (urCount > 0 && urVar > 0));

  const fixedItems = item.fixedCostItems;
  const hasItemizedFixed = Array.isArray(fixedItems) && fixedItems.length > 0;
  const margin = base != null ? Math.max(0, service - base - stampMinor) : 0;

  if (canSplitBase && base != null) {
    if (hasItemizedFixed) {
      for (const fi of fixedItems!) {
        if (fi.amountMinor > 0) {
          detailLines.push({ type: "fixed_component", amountMinor: fi.amountMinor, label: fi.label || undefined });
        }
      }
    } else if (fixed > 0) {
      const fixedDisplay = Math.max(0, fixed - digitalAdjustment);
      if (fixedDisplay > 0) {
        detailLines.push({ type: "fixed_component", amountMinor: fixedDisplay });
      }
    }
    if (urCount > 0 && urVar > 0) {
      detailLines.push({ type: "ur_component", amountMinor: urVar, urUnits: urCount, urType: item.urType });
    }
    if (margin > 0) {
      detailLines.push({ type: "service_fee", amountMinor: margin });
    }
  } else if (base != null) {
    const baseDisplay = Math.max(0, base - digitalAdjustment);
    detailLines.push({ type: "regulated_base", amountMinor: baseDisplay });
    if (margin > 0) {
      detailLines.push({ type: "service_fee", amountMinor: margin });
    }
  }

  if (stampMinor > 0) {
    detailLines.push({ type: "professional_stamp", amountMinor: stampMinor });
  }

  const { deliveryIncludedMinor } = deliveryDisplayFlags(fee, fulfillmentMode);

  return {
    currency,
    detailLines,
    totalMinor,
    deliveryIncludedMinor,
  };
}

/**
 * Line items for per-file products (e.g. income certificate). See the doc on
 * `buildFlatWizardPricingBreakdown` for the row-sum invariant.
 */
export function buildPerFileWizardPricingBreakdown(
  item: CertificatePricingCatalogItem | undefined,
  billableFileCount: number,
  fulfillmentMode?: string
): CertificateWizardPricingBreakdownData | null {
  if (!item) return null;
  const service =
    item.serviceFinalAmountMinor ?? item.finalAmountMinor ?? item.baseAmountMinor;
  if (service == null) return null;
  const currency = item.currency;
  const perFile = item.perFileStorageAmountMinor ?? 0;
  const base = item.baseAmountMinor;
  const fee = item.deliveryFeeMinor ?? 0;
  const stampMinor = item.professionalStampMinor ?? 0;
  const totalMinor =
    estimatePerFileCertificateTotalMinor(item, billableFileCount, fulfillmentMode) ??
    (() => {
      const baseAmount = catalogCustomerAmountMinor(item);
      return baseAmount != null ? applyCatalogDigitalDeliveryDeduction(baseAmount, item, fulfillmentMode) : null;
    })();
  if (totalMinor == null) return null;

  const { deliveryIncludedMinor } = deliveryDisplayFlags(fee, fulfillmentMode);

  const detailLines: CertificateWizardPricingDetailLine[] = [];

  const storageMinor =
    item.pricingModel === "per_file" && perFile > 0 && billableFileCount >= 1
      ? billableFileCount * perFile
      : 0;

  const margin = base != null ? Math.max(0, service - base - stampMinor) : 0;
  const isDigitalOnly = fulfillmentMode === "digital_only";
  const digitalAdjustment = isDigitalOnly ? fee : 0;
  const baseDisplay = base != null ? Math.max(0, base - digitalAdjustment) : null;

  if (item.pricingModel === "per_file") {
    if (baseDisplay != null) {
      detailLines.push({ type: "income_base", amountMinor: baseDisplay });
      if (storageMinor > 0) {
        detailLines.push({
          type: "income_margin_and_storage",
          amountMinor: margin + storageMinor,
          documentCount: billableFileCount,
        });
      } else if (margin > 0) {
        detailLines.push({ type: "income_service_margin", amountMinor: margin });
      }
    } else {
      detailLines.push({
        type: "certificate_service",
        amountMinor:
          Math.max(0, service - stampMinor - digitalAdjustment) + storageMinor,
      });
    }
  } else {
    detailLines.push({
      type: "certificate_service",
      amountMinor: Math.max(0, service - stampMinor - digitalAdjustment),
    });
  }

  if (stampMinor > 0) {
    detailLines.push({ type: "professional_stamp", amountMinor: stampMinor });
  }

  return {
    currency,
    detailLines,
    totalMinor,
    deliveryIncludedMinor,
  };
}

// ── Product filter for internalRequest nodes ────────────────────────────

export interface InternalRequestProductMatch {
  intent: CertificateRequestIntentDto;
  catalogItem: CertificatePricingCatalogItem | null;
}

/**
 * Fetches intents + catalog and returns the subset that matches the given
 * filter (flowKey and/or targetTypes). Used by InternalRequestNodeView to
 * decide which platform products to offer.
 */
export async function getFilteredProducts(filter: {
  flowKey?: string;
  targetTypes?: string[];
}): Promise<InternalRequestProductMatch[]> {
  const catalog = await getCertificatePricingCatalog();

  // Filter catalog items directly by targetType
  let filtered = catalog;

  if (filter.targetTypes && filter.targetTypes.length > 0) {
    filtered = filtered.filter((item) => filter.targetTypes!.includes(item.targetType));
  }

  // If no targetTypes filter, try to narrow by flowKey via intents
  if (!filter.targetTypes?.length && filter.flowKey) {
    const intents = await getCertificateRequestIntents();
    const activeIntentSlugs = intents
      .filter((i) => i.status === "active" && i.flowKey === filter.flowKey)
      .map((i) => i.slug);
    // Intents are categories — match catalog items whose targetType starts with
    // or belongs to the category. Since the mapping isn't 1:1, include all
    // catalog items when the flow has active intents.
    if (activeIntentSlugs.length > 0) {
      // All catalog items are valid for the flow
    } else {
      filtered = [];
    }
  }

  return filtered
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => ({
      intent: { slug: item.targetType, status: "active" as const, flowKey: filter.flowKey ?? "", sortOrder: item.sortOrder },
      catalogItem: item,
    }));
}
